import React, { useState } from "react";
import { Send, Loader2, Mail, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { MissingVars } from "./SendForm";

export default function RelancePiecesButton({ dossierId, onSent }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const generate = async (opts = {}) => {
    setBusy(true);
    try { setDraft((await api.post(`/dossiers/${dossierId}/relance-pieces/draft`, null, { params: opts })).data); } catch (e) { toast.error(e?.response?.data?.detail || "Rédaction impossible"); setOpen(false); } finally { setBusy(false); }
  };
  const start = () => { setOpen(true); setDraft(null); generate(); };
  const send = async () => {
    setSending(true);
    try {
      if (`${draft.subject}\n${draft.body}`.includes("[à compléter") && !window.confirm("Certaines variables sont encore « à compléter ». Envoyer quand même ?")) { setSending(false); return; }
      await api.post(`/dossiers/${dossierId}/relance-pieces/send`, { to: draft.to, subject: draft.subject, body: draft.body, cc: (draft.ccText ?? draft.cc.join(", ")).split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Relance envoyée");
      setOpen(false);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally {
      setSending(false);
    }
  };
  const set = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });

  return (
    <>
      <button onClick={start} className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-transparent bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold transition-colors" title="Préparer la relance par email" data-testid="relance-pieces-btn">
        <Mail className="w-3.5 h-3.5" /> Relance mail
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !sending && setOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl border border-norea-border" onClick={(e) => e.stopPropagation()} data-testid="relance-pieces-modal">
            <div className="px-5 py-3 border-b border-norea-border flex items-center justify-between">
              <div className="font-heading text-lg font-medium">Relance des pièces manquantes</div>
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-norea-surfaceHover" data-testid="relance-close"><X className="w-4 h-4" /></button>
            </div>
            {!draft ? (
              <div className="p-10 text-center text-sm text-norea-textMuted" data-testid="relance-loading"><Loader2 className="w-4 h-4 animate-spin inline mr-1.5" /> Rédaction de l'email…</div>
            ) : (
              <div className="p-5 space-y-3">
                {draft.templates?.length > 0 && (
                  <label className="block text-xs font-medium">Modèle
                    <select value={draft.template_id || ""} onChange={(e) => e.target.value && generate({ template_id: e.target.value })} className="input-field text-sm mt-1" data-testid="relance-template">
                      {!draft.template_id && <option value="">Rédigé par l'IA</option>}
                      {draft.templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </label>
                )}
                <MissingVars missing={draft.missing_vars} testid="relance-missing-vars" />
                <label className="block text-xs font-medium">À (client)
                  <input value={draft.to} onChange={set("to")} placeholder="email du client" className="input-field text-sm mt-1" data-testid="relance-to" />
                </label>
                <label className="block text-xs font-medium">Copie (régie{draft.regie ? ` : ${draft.regie}` : ""})
                  <input value={draft.ccText ?? draft.cc.join(", ")} onChange={(e) => setDraft({ ...draft, ccText: e.target.value })} className="input-field text-sm mt-1" data-testid="relance-cc" />
                </label>
                <label className="block text-xs font-medium">Objet
                  <input value={draft.subject} onChange={set("subject")} className="input-field text-sm mt-1" data-testid="relance-subject" />
                </label>
                <label className="block text-xs font-medium">Message
                  <textarea value={draft.body} onChange={set("body")} rows={11} className="input-field text-sm mt-1 font-sans" data-testid="relance-body" />
                </label>
                <div className="flex items-center justify-between pt-1">
                  <button onClick={() => generate({ ai: true })} disabled={busy} className="btn-secondary text-xs" data-testid="relance-regenerate">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Réécrire avec l'IA</button>
                  <button onClick={send} disabled={sending || !draft.to} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-semibold" data-testid="relance-send">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
